import config from "@/libs/config";
import { z } from "zod";
import { useEffect, useState } from "react";
import { FrontendErrorMsgs } from "@/libs/errors";

/**
 * Feature flags schema
 * @description Feature flags schema for zod
 */
const FeatureFlagSchema = z
  .object({
    Enabled: z.boolean(),
  })
  .required();

const FeatureFlagsSchema = z.object({
  CollectCancelReason: FeatureFlagSchema.required(),
  KenAllAddressAssist: FeatureFlagSchema.required(), // This feature completely released, so we need to remove this feature flag in the future
  // Add more feature flags here
});

type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;

/**
 * useFeatureFlags hook
 * @returns { featureFlags: FeatureFlags | null, loading: boolean, error: Error | null }
 * @description A hook that fetches feature flags from feature flag store. \
 * Currently, it fetches feature flags from environment variables. \
 * in the future, it should fetch feature flags from a feature flag store such as App Config, Launch Darkly, etc
 */
const useFeatureFlags = () => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const defaultFlags: FeatureFlags = {
      CollectCancelReason: {
        Enabled: false,
      },
      KenAllAddressAssist: {
        Enabled: true,
      },
    };

    // Load feature flags from environment variables
    const rawFlags = {
      CollectCancelReason: {
        Enabled: config.FEATURE_FLAG_COLLECT_CANCEL_REASON,
      },
      KenAllAddressAssist: {
        Enabled: config.FEATURE_FLAG_KENALL_ADDRESS_ASSIST,
      },
    };

    try {
      // Perform type checking and parsing using zod
      const parsedFlags = FeatureFlagsSchema.parse(rawFlags);
      setFeatureFlags(parsedFlags);
    } catch (err) {
      // [TODO] This hook shouldn't return an error because this basic component is expected that must be succeeded.
      // However, it might happen that the feature flag is not set correctly in the environment variables or other Feature Flag Store.
      // Therefore we need to notify the error to the developer.
      console.error("Failed to parse feature flags", err);
      setFeatureFlags(defaultFlags); // Set default flags if there is an error
    } finally {
      setLoading(false);
    }
  }, []);

  return { featureFlags, loading, error };
};

export default useFeatureFlags;
